// SPDX-License-Identifier: AGPL-3.0-or-later

import type {
	SearchProviderMenuEngine,
	SearchProviderMenuState,
} from '@app/features/ui/action_menu/items/SearchProviderMenuUtils';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {MenuItemSubmenu} from '@app/features/ui/action_menu/MenuItemSubmenu';
import {useLingui} from '@lingui/react/macro';
import type React from 'react';

interface SearchProviderContextMenuItemsProps<T extends SearchProviderMenuEngine> {
	state: SearchProviderMenuState<T>;
	defaultLabel: string;
	renderIcon: () => React.ReactNode;
	onDefaultSearch: () => void;
	onSearchWithEngine: (engine: T) => void;
}
export function SearchProviderContextMenuItems<T extends SearchProviderMenuEngine>({
	state,
	defaultLabel,
	renderIcon,
	onDefaultSearch,
	onSearchWithEngine,
}: SearchProviderContextMenuItemsProps<T>): React.ReactNode {
	const {t} = useLingui();
	if (state.enabledEngines.length === 0) {
		return null;
	}
	if (state.alternateEngines.length > 0) {
		const defaultEngine = state.defaultEngine;
		return (
			<MenuItemSubmenu
				label={defaultLabel}
				onTriggerSelect={onDefaultSearch}
				render={() => (
					<MenuGroup>
						{defaultEngine && (
							<MenuItem hint={t`Default`} onClick={() => onSearchWithEngine(defaultEngine)}>
								{defaultEngine.name}
							</MenuItem>
						)}
						{state.alternateEngines.map((engine) => (
							<MenuItem key={engine.id} onClick={() => onSearchWithEngine(engine)}>
								{engine.name}
							</MenuItem>
						))}
					</MenuGroup>
				)}
			/>
		);
	}
	return (
		<MenuItem icon={renderIcon()} onClick={onDefaultSearch}>
			{defaultLabel}
		</MenuItem>
	);
}
